import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Roadmap #10 slice 1 — typed wrapper for the super-admin Platform Finance
 * callable. The server re-validates with its own Zod; this wrapper gives the
 * dashboard fast feedback before a round-trip and a precise result type.
 *
 * Each section carries its own `ok` flag: a `false` means that sub-read
 * failed server-side and the UI must show retry, NOT a false RM 0.00.
 */

const PeriodSchema = z.enum(['this_month', 'last_month', 'all_time']);
export type FinancePeriod = z.infer<typeof PeriodSchema>;

export const GetPlatformFinanceSummaryInputSchema = z.object({
  brandId: z.string().min(1),
  period: PeriodSchema.default('this_month'),
});
export type GetPlatformFinanceSummaryInput = z.infer<typeof GetPlatformFinanceSummaryInputSchema>;

const RevenueSectionSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    periodSen: z.number().int(),
    allTimeSen: z.number().int(),
    sstPeriodSen: z.number().int(),
    sstAllTimeSen: z.number().int(),
  }),
  z.object({ ok: z.literal(false) }),
]);
export type FinanceRevenueSection = z.infer<typeof RevenueSectionSchema>;

const TenantsSectionSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    active: z.number().int().nonnegative(),
    trial: z.number().int().nonnegative(),
    pastDue: z.number().int().nonnegative(),
    suspended: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    mrrSen: z.number().int().nonnegative(),
  }),
  z.object({ ok: z.literal(false) }),
]);
export type FinanceTenantsSection = z.infer<typeof TenantsSectionSchema>;

// Slice 2 — what the platform still owes its agents + recruiters.
const LiabilitiesSectionSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    agentsUnpaidSen: z.number().int().nonnegative(),
    recruitersUnpaidSen: z.number().int().nonnegative(),
    agentsPayableNowSen: z.number().int().nonnegative(),
    recruitersPayableNowSen: z.number().int().nonnegative(),
  }),
  z.object({ ok: z.literal(false) }),
]);
export type FinanceLiabilitiesSection = z.infer<typeof LiabilitiesSectionSchema>;

// Slice 2 — payout-batch health, bucketed by status + recipient kind.
const PayoutsSectionSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    pendingSen: z.number().int().nonnegative(),
    paidAllTimeSen: z.number().int().nonnegative(),
    pendingCount: z.number().int().nonnegative(),
    byKind: z.object({
      agent: z.object({ pendingSen: z.number().int().nonnegative() }),
      recruiter: z.object({ pendingSen: z.number().int().nonnegative() }),
      tenant: z.object({ pendingSen: z.number().int().nonnegative() }),
    }),
  }),
  z.object({ ok: z.literal(false) }),
]);
export type FinancePayoutsSection = z.infer<typeof PayoutsSectionSchema>;

export const GetPlatformFinanceSummaryOutputSchema = z.object({
  brandId: z.string(),
  period: PeriodSchema,
  computedAtMs: z.number().int().nonnegative(),
  revenue: RevenueSectionSchema,
  tenants: TenantsSectionSchema,
  liabilities: LiabilitiesSectionSchema,
  payouts: PayoutsSectionSchema,
});
export type GetPlatformFinanceSummaryOutput = z.infer<typeof GetPlatformFinanceSummaryOutputSchema>;

const fn: HttpsCallable<GetPlatformFinanceSummaryInput, GetPlatformFinanceSummaryOutput> =
  httpsCallable(functions, 'getPlatformFinanceSummary');

export async function callGetPlatformFinanceSummary(
  input: GetPlatformFinanceSummaryInput,
): Promise<GetPlatformFinanceSummaryOutput> {
  const parsed = GetPlatformFinanceSummaryInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError(
      'getPlatformFinanceSummary.input',
      parsed.error.flatten(),
    );
  }
  const result = await fn(parsed.data);
  const out = GetPlatformFinanceSummaryOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('getPlatformFinanceSummary.output', out.error.flatten());
  }
  return out.data;
}
